import { useCallback, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { KnowledgeEditorSource } from 'apps/aiAutomation/components/KnowledgeEditor/lib/types'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import {
  AiAutomationKnowledgeActions,
  AiAutomationKnowledgeSelectors,
} from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  KnowledgeEntry,
  KnowledgeEntryDraft,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

export const useEditor = ({
  setIsEditModeEnabled,
  entryId,
  onSave,
  source,
}: {
  setIsEditModeEnabled: (enabled: boolean) => void
  entryId?: string | null
  source: KnowledgeEditorSource
  onSave?: (entry: KnowledgeEntry | KnowledgeEntryDraft, isNew: boolean) => void
}) => {
  const dispatch = useAppDispatch()

  const selectedEntry = useAppSelector((state) =>
    entryId ? AiAutomationKnowledgeSelectors.getItemById(state, entryId) : null,
  )
  const isLoading = useAppSelector((state) =>
    AiAutomationKnowledgeSelectors.getIsItemLoading(state, entryId ?? null),
  )

  const isNewEntry = !selectedEntry

  const save = useCallback(
    async (entry: KnowledgeEntry | KnowledgeEntryDraft, hasBeenChanged?: boolean) => {
      if (isNewEntry) {
        await dispatch(
          AiAutomationKnowledgeActions.addEntryFx(entry as KnowledgeEntryDraft),
        ).unwrap()
      } else {
        await dispatch(AiAutomationKnowledgeActions.editEntryFx(entry as KnowledgeEntry)).unwrap()
      }

      if (isNewEntry) {
        AiAutomationAnalytics.logKnowledgeEntryCreated({ id: entry.id, hasBeenChanged, source })
      } else {
        AiAutomationAnalytics.logKnowledgeEntryUpdated({ id: entry.id, hasBeenChanged, source })
      }

      dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
      setIsEditModeEnabled(false)
      onSave?.(entry, isNewEntry)
    },
    [dispatch, isNewEntry, setIsEditModeEnabled, onSave, source],
  )

  useEffect(() => {
    if (entryId) {
      dispatch(AiAutomationKnowledgeActions.ensureEntryLoadedFx(entryId))
    }
  }, [dispatch, entryId])

  return {
    selectedEntry,
    isLoading,
    save,
  }
}
